import {
	RiCloseLine,
	RiMenuLine,
	RiMoonLine,
	RiSunLine,
} from '@remixicon/react';
import {
	Fragment,
	type MouseEvent,
	type PropsWithChildren,
	type ReactNode,
	useEffect,
	useRef,
} from 'react';

import { ThreeSixtyLogotype } from '@shared/atoms/threesixty-logotype';
import { useCurrentColorScheme } from '@shared/hooks/color-scheme';
import { useApplicationState } from '@shared/providers/application-state';
import { useNavigationEvent } from '@shared/router/hooks/navigation';
import Link from '@shared/router/link';
import { useCurrentRoutePath } from '@shared/router/state';

import { Button, type ButtonVariant } from '@playbook/atoms/button';
import { IconButton } from '@playbook/atoms/icon-button';
import {
	MaxMediaQuery,
	MinMediaQuery,
} from '@playbook/modules/layout/media-query';
import { cn } from '@playbook/utils';

import css from './styles.module.css';

interface NavigationItem {
	href: string;
	label: string;
	/** Runs before the navigation. The element differs between a link and a button. */
	onClick?: (event: MouseEvent<HTMLElement>) => void;

	leadingIcon?: ReactNode;
	trailingIcon?: ReactNode;
}

export type NavigationVariant = 'floating' | 'sticky';

interface NavigationProps extends PropsWithChildren {
	variant?: NavigationVariant;

	hideBrandIcon?: boolean;
	displayName: string;
	logotype: ReactNode;
	rootPath?: string;

	items?: NavigationItem[];

	renderCompactWith?: ReactNode;
	renderActionsWith?: ReactNode;

	primaryAction?: NavigationItem;
	secondaryAction?: NavigationItem;
}

/**
 *	The width below which the compact bar replaces the complete navigation.
 *	Exported for whatever renders into the navigation and has to know which
 *	of the two it is in.
 */
export const NavigationCompactBreakpoint: string = '960px';

export function Navigation(properties: NavigationProps): ReactNode {
	const breakAt: string = NavigationCompactBreakpoint;

	return (
		<Fragment>
			<MinMediaQuery breakpoint={breakAt}>
				<CompleteNavigation {...properties} />
			</MinMediaQuery>
			<MaxMediaQuery breakpoint={`calc(${breakAt} - 1px)`}>
				<CompactNavigation {...properties} />
			</MaxMediaQuery>
		</Fragment>
	);
}

interface NavigationActionProps {
	item?: NavigationItem;
	variant?: ButtonVariant;
	onNavigate?: () => void;
}

function NavigationAction({
	item,
	variant,
	onNavigate,
}: NavigationActionProps): ReactNode {
	const navigate = useNavigationEvent();

	if (!item) {
		return null;
	}

	return (
		<Button
			label={item.label}
			variant={variant}
			leadingIcon={item?.leadingIcon}
			trailingIcon={item?.trailingIcon}
			onClick={(event) => {
				// The item's own handler runs first, as it does for the links
				// in the menu: an action that changes what the next page shows
				// has to land before the navigation does.
				item.onClick?.(event);
				onNavigate?.();
				navigate(item.href);
			}}
		/>
	);
}

function ColorSchemeToggle(): ReactNode {
	const { setColorScheme } = useApplicationState();
	const currentColorScheme = useCurrentColorScheme();

	const toggleColorScheme = () => {
		if (currentColorScheme === 'dark') {
			setColorScheme('light');
		} else {
			setColorScheme('dark');
		}
	};

	return (
		<IconButton
			variant="ghost"
			label={
				currentColorScheme === 'dark'
					? 'Switch to light mode'
					: 'Switch to dark mode'
			}
			icon={currentColorScheme === 'dark' ? <RiSunLine /> : <RiMoonLine />}
			onClick={toggleColorScheme}
		/>
	);
}

function CompleteNavigation({
	variant = 'floating',

	hideBrandIcon,
	displayName,
	logotype,
	rootPath,

	items = [],

	renderActionsWith,

	primaryAction,
	secondaryAction,
}: NavigationProps): ReactNode {
	return (
		<div className={cn(css.root, css[variant])}>
			<nav className={cn(css.navigation, css[variant])}>
				{!hideBrandIcon && <ThreeSixtyLogotype className={css.brandLogotype} />}
				<Link href={rootPath} className={css.logotype}>
					{logotype}
					<h1 className={css.displayName}>{displayName}</h1>
				</Link>
				<section>
					{items.length > 0 && (
						<ul className={css.menu}>
							{items.map((item, n) => (
								<li key={n}>
									<Link
										href={item.href}
										className={css.menuItem}
										onClick={item.onClick}>
										{item?.leadingIcon}
										{item.label}
										{item?.trailingIcon}
									</Link>
								</li>
							))}
						</ul>
					)}
				</section>
				<aside className={css.actions}>
					<ColorSchemeToggle />
					{renderActionsWith}
					<NavigationAction item={secondaryAction} variant="secondary" />
					<NavigationAction item={primaryAction} variant="primary" />
				</aside>
			</nav>
		</div>
	);
}

interface CompactNavigationBarProps extends Pick<
	NavigationProps,
	| 'hideBrandIcon'
	| 'displayName'
	| 'logotype'
	| 'rootPath'
	| 'renderActionsWith'
> {
	action: ReactNode;
}

function CompactNavigationBar({
	hideBrandIcon,
	displayName,
	logotype,
	rootPath,

	action,

	renderActionsWith,
}: CompactNavigationBarProps): ReactNode {
	return (
		<Fragment>
			{!hideBrandIcon && <ThreeSixtyLogotype className={css.brandLogotype} />}
			<Link href={rootPath} className={css.logotype}>
				{logotype}
				<h1 className={css.displayName}>{displayName}</h1>
			</Link>

			<aside className={css.actions}>
				{renderActionsWith}
				{action}
			</aside>
		</Fragment>
	);
}

function CompactNavigation({
	variant = 'floating',

	hideBrandIcon,
	displayName,
	logotype,
	rootPath,

	items = [],

	renderCompactWith,
	renderActionsWith,

	primaryAction,
	secondaryAction,
}: NavigationProps): ReactNode {
	const menuRef = useRef<HTMLDialogElement>(null);

	const openMenu = () => menuRef.current?.showModal();
	const closeMenu = () => menuRef.current?.close();

	const currentPath = useCurrentRoutePath();

	useEffect(() => {
		menuRef.current?.close();
	}, [currentPath]);

	return (
		<div className={cn(css.root, css[variant])}>
			<nav className={cn(css.navigation, css.compact, css[variant])}>
				<CompactNavigationBar
					hideBrandIcon={hideBrandIcon}
					displayName={displayName}
					logotype={logotype}
					rootPath={rootPath}
					action={
						<Fragment>
							<ColorSchemeToggle />
							{renderActionsWith}
							<IconButton
								variant="tertiary"
								label="Open menu"
								icon={<RiMenuLine />}
								onClick={openMenu}
							/>
						</Fragment>
					}
				/>
			</nav>

			<dialog ref={menuRef} className={cn(css.menuSheet, css[variant])}>
				<div className={cn(css.navigation, css.compact, css[variant])}>
					<CompactNavigationBar
						hideBrandIcon={hideBrandIcon}
						displayName={displayName}
						logotype={logotype}
						action={
							<IconButton
								variant="tertiary"
								label="Close menu"
								icon={<RiCloseLine />}
								onClick={closeMenu}
							/>
						}
					/>
				</div>

				<nav>
					{!renderCompactWith && items.length > 0 && (
						<ul className={cn(css.menu, css.stacked)}>
							{items.map((item, n) => (
								<li key={n}>
									<Link
										href={item.href}
										onClick={(event) => {
											item.onClick?.(event);
											closeMenu();
										}}>
										{item.label}
									</Link>
								</li>
							))}
						</ul>
					)}
					{renderCompactWith}
				</nav>

				{(primaryAction || secondaryAction) && (
					<aside className={css.sheetActions}>
						<NavigationAction
							item={primaryAction}
							variant="primary"
							onNavigate={closeMenu}
						/>
						<NavigationAction
							item={secondaryAction}
							variant="secondary"
							onNavigate={closeMenu}
						/>
					</aside>
				)}
			</dialog>
		</div>
	);
}
